import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import { alpha, Box, Button, Chip, Container, Stack, Typography, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import StyledLink from "../../component/StyledLink";
import {
  courseCatalogCategories,
  getCourseCatalogCategory,
  getCourseCatalogCategoryPath,
} from "./const/courseCatalog";

export default function CourseListPage() {
  const theme = useTheme();
  const { categoryId } = useParams();
  const activeCategory = getCourseCatalogCategory(categoryId);

  return (
    <Box
      sx={{
        minHeight: 1,
        bgcolor: "background.default",
        backgroundImage: `
          radial-gradient(ellipse 58% 22% at 22% 5%, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0)} 76%),
          radial-gradient(ellipse 50% 18% at 88% 42%, ${alpha(theme.palette.info.dark, 0.1)} 0%, ${alpha(theme.palette.info.dark, 0)} 74%),
          linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.72)} 0%, ${theme.palette.background.default} 20%, ${
          theme.palette.background.default
        } 100%)
        `,
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 }, py: { xs: 5, md: 8 } }}>
        <Stack spacing={{ xs: 4, md: 5 }}>
          <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
            <Typography variant="h2" color="primary.contrastText">
              課程列表
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {courseCatalogCategories.map((category) => {
              const isActive = category.id === activeCategory.id;
              return (
                <Button
                  key={category.id}
                  component={StyledLink}
                  to={getCourseCatalogCategoryPath(category.id)}
                  sx={{
                    borderRadius: 0,
                    px: 2,
                    py: 1,
                    border: `1px solid ${alpha(theme.palette.primary.contrastText, isActive ? 0.26 : 0.12)}`,
                    color: isActive ? "primary.contrastText" : alpha(theme.palette.primary.contrastText, 0.72),
                    bgcolor: isActive ? alpha(theme.palette.info.main, 0.14) : alpha(theme.palette.primary.contrastText, 0.035),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                    },
                  }}
                >
                  {category.label}
                </Button>
              );
            })}
          </Stack>

          <Stack spacing={3}>
            {activeCategory.courses.length === 0 ? (
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.035),
                }}
              >
                <Typography variant="h5" color="primary.contrastText">
                  暫未有可瀏覽課程
                </Typography>
              </Box>
            ) : (
              activeCategory.courses.map((course, index) => <CourseListItem key={course.to} course={course} index={index} />)
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function CourseListItem({
  course,
  index,
}: Readonly<{
  course: {
    title: string;
    caption: string;
    description: string;
    status: string;
    to: string;
    media?: {
      kind: "image";
      src: string;
      alt: string;
    };
  };
  index: number;
}>) {
  const theme = useTheme();

  return (
    <Box
      component={StyledLink}
      to={course.to}
      sx={{
        display: "block",
        border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
        bgcolor: alpha(theme.palette.primary.contrastText, 0.04),
        color: "primary.contrastText",
        overflow: "hidden",
        transition: theme.transitions.create(["background-color", "border-color"], {
          duration: theme.transitions.duration.shortest,
        }),
        "&:hover": {
          bgcolor: alpha(theme.palette.info.main, 0.1),
          borderColor: alpha(theme.palette.info.main, 0.3),
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px minmax(0, 1fr) auto" },
          minHeight: { xs: "auto", md: 230 },
        }}
      >
        <CourseThumbnail index={index} media={course.media} />
        <Stack spacing={1.25} sx={{ p: { xs: 3, md: 3.5 } }}>
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="h4">{course.title}</Typography>
            <Chip
              label={course.status}
              variant="outlined"
              sx={{
                borderRadius: 0,
                borderColor: alpha(theme.palette.primary.contrastText, 0.2),
                color: alpha(theme.palette.primary.contrastText, 0.82),
                "& .MuiChip-label": {
                  fontFamily: theme.typography.fontFamily,
                },
              }}
            />
          </Stack>
          <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.68) }}>
            {course.caption}
          </Typography>
          <Typography variant="body1" sx={{ color: alpha(theme.palette.primary.contrastText, 0.76) }}>
            {course.description}
          </Typography>
        </Stack>
        <ArrowForwardRoundedIcon
          sx={{
            alignSelf: "center",
            justifySelf: { xs: "start", md: "center" },
            m: { xs: 3, md: 3.5 },
            color: alpha(theme.palette.info.light, 0.9),
          }}
        />
      </Box>
    </Box>
  );
}

function CourseThumbnail({
  index,
  media,
}: Readonly<{
  index: number;
  media?: {
    kind: "image";
    src: string;
    alt: string;
  };
}>) {
  const theme = useTheme();
  const accentColor = index % 2 === 0 ? theme.palette.info.main : theme.palette.primary.light;
  const codeLines = index % 2 === 0 ? ["const skill = build();", "deploy(careerPath);"] : ["class CareerPath {", "  startWith(fullStack);", "}"];

  if (media) {
    return <CourseMediaThumbnail media={media} />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 210, md: 1 },
        overflow: "hidden",
        borderRight: { md: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}` },
        background: `
          radial-gradient(circle at 18% 24%, ${alpha(accentColor, 0.34)} 0%, ${alpha(accentColor, 0)} 38%),
          radial-gradient(circle at 82% 72%, ${alpha(theme.palette.info.dark, 0.42)} 0%, ${alpha(theme.palette.info.dark, 0)} 46%),
          linear-gradient(135deg, ${alpha(theme.palette.primary.contrastText, 0.055)} 0%, ${alpha(theme.palette.primary.dark, 0.58)} 100%)
        `,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: { xs: 22, md: 26 },
          border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.dark, 0.26),
          backdropFilter: "blur(10px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: -36,
          bottom: -42,
          width: 180,
          height: 180,
          border: `1px solid ${alpha(accentColor, 0.18)}`,
          transform: "rotate(18deg)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 34,
          right: 30,
          width: 92,
          height: 92,
          border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.08)}`,
          transform: "rotate(45deg)",
        }}
      />
      <Stack
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
          height: 1,
          minHeight: { xs: 210, md: 1 },
          p: 3,
          justifyContent: "flex-start",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <CodeRoundedIcon sx={{ fontSize: 42, color: alpha(theme.palette.info.light, 0.92) }} />
          <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.contrastText, 0.48) }}>
            0{index + 1}
          </Typography>
        </Stack>
        <Stack
          spacing={0.75}
          sx={{
            mt: { xs: 2, md: 3 },
            p: 1.5,
            borderLeft: `2px solid ${alpha(accentColor, 0.7)}`,
            bgcolor: alpha(theme.palette.primary.dark, 0.22),
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TerminalRoundedIcon sx={{ fontSize: 16, color: alpha(theme.palette.info.light, 0.78) }} />
            <Typography variant="caption" sx={{ color: alpha(theme.palette.primary.contrastText, 0.68) }}>
              training.ts
            </Typography>
          </Stack>
          {codeLines.map((line) => (
            <Typography key={line} variant="caption" sx={{ color: alpha(theme.palette.primary.contrastText, 0.72) }}>
              {line}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

function CourseMediaThumbnail({
  media,
}: Readonly<{
  media: {
    kind: "image";
    src: string;
    alt: string;
  };
}>) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 260, md: 1 },
        overflow: "hidden",
        borderRight: { md: `1px solid ${alpha(theme.palette.primary.contrastText, 0.1)}` },
        bgcolor: alpha(theme.palette.primary.dark, 0.32),
      }}
    >
      <Box
        component="img"
        src={media.src}
        alt={media.alt}
        sx={{
          width: 1,
          height: 1,
          minHeight: { xs: 260, md: 230 },
          objectFit: "cover",
          display: "block",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0)} 0%, ${alpha(theme.palette.primary.dark, 0.28)} 100%)`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
